<template>
  <div>
    <h2 v-if="!hideHeader" class="mb-4">{{ t('customer') }}</h2>
    <div class="grid gap-4 md:grid-cols-3">
      <InputField
        v-if="!hideEmail"
        :name="`${namePrefix}${contactEmail ? 'contactEmail' : 'email'}`"
        :label="t(contactEmail ? 'contactEmail' : 'email')"
        :tooltip="contactEmail ? (emailTooltip ?? t('contactEmailInformation')) : emailTooltip"
        :disabled="readOnlyContactEmail"
        inputmode="email"
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
      />
      <InputField :name="`${namePrefix}company`" :label="t('company')" />
      <InputField
        :name="`${namePrefix}vatId`"
        :label="t('vatId')"
        autocapitalize="characters"
        autocorrect="off"
        spellcheck="false"
      />
      <SelectField
        :name="`${namePrefix}gender`"
        :label="t('gender')"
        :options="genderOptions"
        class="col-start-1"
      />
      <InputField :name="`${namePrefix}firstName`" :label="t('firstName')" />
      <InputField :name="`${namePrefix}lastName`" :label="t('lastName')" />
      <InputField :name="`${namePrefix}phone`" :label="t('phone')" :disabled="readOnlyPhone" />
      <DatepickerField :name="`${namePrefix}dateOfBirth`" :label="t('dateOfBirth')" calendar-date />
      <div class="flex gap-x-2">
        <SelectField
          class="flex-1"
          :name="`${namePrefix}preferredLocal`"
          :label="t('preferredLocal')"
          :options="preferredLocalOptions"
        />
      </div>
      <InputField class="w-full grow" :name="`${namePrefix}street`" :label="t('street')" />
      <div class="flex gap-2">
        <InputField class="basis-2/5" :name="`${namePrefix}zip`" :label="t('zip')" />
        <InputField class="basis-3/5" :name="`${namePrefix}city`" :label="t('city')" />
      </div>
      <div data-testid="country-select">
        <SearchableSelectField
          :name="`${namePrefix}country`"
          :label="t('country')"
          :options="countries"
          :track-by="['label', 'labelEn', 'labelShort', 'value', 'alternativeNames']"
        />
        <div
          v-if="formValues.country && !countries.some((c) => c.value === formValues.country)"
          class="text-bittersweet-600"
        >
          {{ t('invalidCountry') }}: {{ formValues.country }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import InputField from '@/components/InputField.vue';
import DatepickerField from '@/components/DatepickerField.vue';
import SelectField from '@/components/SelectField.vue';
import { Local } from '@/entities/auth/local.enum';
import { Gender } from '@/entities/auth/gender.enum';
import SearchableSelectField from '@/components/SearchableSelectField.vue';
import { getCountries } from '@/utils/countries';
import { useFormValues } from 'vee-validate';

const props = defineProps<{
  name?: string;
  hideEmail?: boolean;
  hideHeader?: boolean;
  contactEmail?: boolean;
  emailTooltip?: string;
  readOnlyPhone?: boolean;
  readOnlyContactEmail?: boolean;
}>();

const { t, locale } = useI18n();

const countries = computed(() => getCountries(locale.value));
const formValues = useFormValues();

const namePrefix = computed(() => (props.name ? `${props.name}.` : ''));

const genderOptions = computed(() =>
  Object.values(Gender).map((value) => ({
    label: t(`gender.${value}`),
    value,
  })),
);

const preferredLocalOptions = computed(() =>
  Object.values(Local).map((value) => ({
    label: t(`locales.${value}`),
    value,
  })),
);
</script>

<i18n lang="json">
{
  "en": {
    "customer": "Customer",
    "email": "Email",
    "contactEmail": "Contact email",
    "contactEmailInformation": "The contract, car-swap protocol, and NPS survey will be sent to this address. Invoices and payment links use the linked customer account email.",
    "gender": "Gender",
    "firstName": "First Name",
    "lastName": "Last Name",
    "company": "Company",
    "vatId": "VAT ID",
    "city": "City",
    "zip": "Zip",
    "street": "Street",
    "phone": "Phone",
    "country": "Country",
    "dateOfBirth": "Date Of Birth",
    "preferredLocal": "Pref. Language",
    "invalidCountry": "Invalid Country"
  },
  "de": {
    "customer": "Kunde",
    "email": "E-Mail",
    "contactEmail": "Kontakt-E-Mail",
    "contactEmailInformation": "Vertrag, Tauschprotokoll und NPS-Umfrage werden an diese Adresse gesendet. Rechnungen und Zahlungslinks nutzen die E-Mail des verknüpften Kundenkontos.",
    "gender": "Geschlecht",
    "firstName": "Vorname",
    "lastName": "Nachname",
    "company": "Firma",
    "vatId": "USt-IdNr.",
    "city": "Stadt",
    "zip": "PLZ",
    "street": "Straße",
    "phone": "Telefon",
    "country": "Land",
    "dateOfBirth": "Geburtsdatum",
    "preferredLocal": "Bev. Sprache",
    "invalidCountry": "Ungültiges Land"
  },
  "es": {
    "customer": "Cliente",
    "email": "Correo electrónico",
    "contactEmail": "Correo de contacto",
    "contactEmailInformation": "El contrato, el protocolo de cambio de coche y la encuesta NPS se enviarán a esta dirección. Las facturas y los enlaces de pago usan el correo de la cuenta vinculada.",
    "gender": "Sexo",
    "firstName": "Nombre",
    "lastName": "Apellidos",
    "company": "Empresa",
    "city": "Ciudad",
    "zip": "Código postal",
    "street": "Calle",
    "phone": "Teléfono",
    "country": "País",
    "dateOfBirth": "Fecha de nacimiento",
    "preferredLocal": "Idioma preferido",
    "invalidCountry": "País no válido"
  }
}
</i18n>
